"use client";

import { type RefObject, useEffect, useRef, useState } from "react";
import Image from "next/image";
import * as THREE from "three";
import { geoEquirectangular, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import type { GeometryObject, Topology } from "topojson-specification";
import landAtlas from "world-atlas/land-110m.json";

const clamp = (value: number, min = 0, max = 1) => Math.min(max, Math.max(min, value));

const getAssetUrl = (path: string): string => {
  if (!path) return path;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("data:")) return path;
  const base = import.meta.env.BASE_URL || "/";
  const prefix = base.endsWith("/") ? base : `${base}/`;
  return `${prefix}${path.startsWith("/") ? path.slice(1) : path}`;
};

export type PlanetMotionPreference = "auto" | "full" | "reduced";
export type PlanetMotionMode = "full" | "reduced";
export type BrandSignal = "precision" | "judgment" | "trust";
export type AICapabilitySignal = "speed" | "pattern" | "repeatability";
export type JudgmentForce = "empathy" | "nuance" | "accountability";
export type OutcomeSignal = "clarity" | "consistency" | "confidence" | "trust";
export type IndustrySignal = "insurance" | "healthcare" | "financial" | "technology" | "consumer" | "education";
export type PerspectiveSignal = "ai" | "human";
export type FaqCategory = "about" | "delivery" | "ai-human";

export type PlanetMotionState = {
  preference: PlanetMotionPreference;
  mode: PlanetMotionMode;
  speed: number;
  precision: number;
  aiCapabilityFocus: AICapabilitySignal | null;
  brandSignal: number;
  brandSignalFocus: BrandSignal | null;
  judgment: number;
  judgmentFocus: JudgmentForce | null;
  outcomes: number;
  outcomeFocus: OutcomeSignal | null;
  outcomeSequence: number;
  industries: number;
  industryFocus: IndustrySignal | null;
  industryPreview: boolean;
  industrySequence: number;
  questions: number;
  questionCategory: FaqCategory | null;
  questionPreview: boolean;
  questionSequence: number;
  perspectives: number;
  perspectiveFocus: PerspectiveSignal | null;
  aiWorld: number;
  humanWorld: number;
  contact: number;
};

type PrototypePlanetProps = {
  motionState: RefObject<PlanetMotionState>;
};

function pointOnSphere(latitude: number, longitude: number, radius: number) {
  const phi = (90 - latitude) * (Math.PI / 180);
  const theta = (longitude + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -(radius * Math.sin(phi) * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * Math.sin(phi) * Math.sin(theta),
  );
}

function createEarthTextures(mobile: boolean) {
  const width = mobile ? 1024 : 2048;
  const height = width / 2;
  const surfaceCanvas = document.createElement("canvas");
  surfaceCanvas.width = width;
  surfaceCanvas.height = height;
  const surface = surfaceCanvas.getContext("2d");
  const cloudCanvas = document.createElement("canvas");
  cloudCanvas.width = width;
  cloudCanvas.height = height;
  const clouds = cloudCanvas.getContext("2d");
  if (!surface || !clouds) return null;

  const ocean = surface.createLinearGradient(0, 0, width, height);
  ocean.addColorStop(0, "#071b32");
  ocean.addColorStop(0.28, "#0b3f67");
  ocean.addColorStop(0.57, "#12638b");
  ocean.addColorStop(1, "#061a34");
  surface.fillStyle = ocean;
  surface.fillRect(0, 0, width, height);

  let seed = 2417;
  const random = () => {
    seed = (seed * 16807) % 2147483647;
    return (seed - 1) / 2147483646;
  };
  surface.save();
  surface.globalCompositeOperation = "screen";
  for (let index = 0; index < (mobile ? 380 : 760); index += 1) {
    const x = random() * width;
    const y = random() * height;
    const radius = 0.4 + random() * 2.1;
    surface.fillStyle = `rgba(91,178,211,${0.018 + random() * 0.026})`;
    surface.beginPath();
    surface.arc(x, y, radius, 0, Math.PI * 2);
    surface.fill();
  }
  surface.restore();

  const topology = landAtlas as unknown as Topology<{ land: GeometryObject }>;
  const land = feature(topology, topology.objects.land);
  const projection = geoEquirectangular()
    .translate([width / 2, height / 2])
    .scale(width / (Math.PI * 2))
    .precision(0.2);
  const landCanvas = document.createElement("canvas");
  landCanvas.width = width;
  landCanvas.height = height;
  const landContext = landCanvas.getContext("2d");
  if (!landContext) return null;
  const landPath = geoPath(projection, landContext);
  landContext.beginPath();
  landPath(land);
  landContext.fillStyle = "#ffffff";
  landContext.fill();
  landContext.globalCompositeOperation = "source-in";
  const terrain = landContext.createLinearGradient(0, 0, 0, height);
  terrain.addColorStop(0, "#dbe5d8");
  terrain.addColorStop(0.16, "#758b66");
  terrain.addColorStop(0.36, "#557c50");
  terrain.addColorStop(0.52, "#2f6b49");
  terrain.addColorStop(0.67, "#697d4f");
  terrain.addColorStop(0.84, "#8f8d76");
  terrain.addColorStop(1, "#e8ece3");
  landContext.fillStyle = terrain;
  landContext.fillRect(0, 0, width, height);

  const dryRegions: Array<[number, number, number, number]> = [
    [0.54, 0.42, 0.105, 0.07],
    [0.68, 0.38, 0.12, 0.055],
    [0.82, 0.69, 0.065, 0.065],
    [0.25, 0.43, 0.07, 0.06],
    [0.4, 0.66, 0.045, 0.065],
  ];
  dryRegions.forEach(([x, y, rx, ry]) => {
    const gradient = landContext.createRadialGradient(
      x * width,
      y * height,
      0,
      x * width,
      y * height,
      Math.max(rx * width, ry * height),
    );
    gradient.addColorStop(0, "rgba(197,156,91,0.9)");
    gradient.addColorStop(0.58, "rgba(156,133,76,0.56)");
    gradient.addColorStop(1, "rgba(113,111,70,0)");
    landContext.save();
    landContext.translate(x * width, y * height);
    landContext.scale(1, ry / rx);
    landContext.translate(-x * width, -y * height);
    landContext.fillStyle = gradient;
    landContext.fillRect(0, 0, width, height * (rx / ry));
    landContext.restore();
  });

  landContext.globalCompositeOperation = "source-over";
  landContext.beginPath();
  landPath(land);
  landContext.strokeStyle = "rgba(220,239,224,0.28)";
  landContext.lineWidth = mobile ? 0.5 : 0.8;
  landContext.stroke();
  surface.drawImage(landCanvas, 0, 0);

  const polar = surface.createLinearGradient(0, 0, 0, height);
  polar.addColorStop(0, "rgba(240,248,249,0.72)");
  polar.addColorStop(0.08, "rgba(225,239,239,0.1)");
  polar.addColorStop(0.16, "rgba(225,239,239,0)");
  polar.addColorStop(0.84, "rgba(225,239,239,0)");
  polar.addColorStop(0.94, "rgba(225,239,239,0.14)");
  polar.addColorStop(1, "rgba(240,248,249,0.74)");
  surface.fillStyle = polar;
  surface.fillRect(0, 0, width, height);

  clouds.clearRect(0, 0, width, height);
  clouds.save();
  clouds.filter = `blur(${mobile ? 5 : 8}px)`;
  for (let band = 0; band < 7; band += 1) {
    const bandY = height * (0.18 + band * 0.105 + (random() - 0.5) * 0.035);
    const pieces = mobile ? 18 : 28;
    for (let index = 0; index < pieces; index += 1) {
      const x = random() * width;
      const rx = width * (0.012 + random() * 0.027);
      const ry = height * (0.007 + random() * 0.018);
      clouds.fillStyle = `rgba(255,255,255,${0.12 + random() * 0.24})`;
      clouds.beginPath();
      clouds.ellipse(x, bandY + (random() - 0.5) * height * 0.07, rx, ry, random() * 0.6, 0, Math.PI * 2);
      clouds.fill();
    }
  }
  clouds.restore();

  const surfaceTexture = new THREE.CanvasTexture(surfaceCanvas);
  surfaceTexture.colorSpace = THREE.SRGBColorSpace;
  surfaceTexture.anisotropy = mobile ? 2 : 4;
  const cloudTexture = new THREE.CanvasTexture(cloudCanvas);
  cloudTexture.colorSpace = THREE.SRGBColorSpace;
  cloudTexture.anisotropy = mobile ? 2 : 4;
  return { cloudTexture, surfaceTexture };
}

type PremiumHumanTextureState = {
  failed: boolean;
  ready: boolean;
};

function loadPremiumHumanSurfaceTextures(maxAnisotropy: number) {
  const state: PremiumHumanTextureState = { failed: false, ready: false };
  const manager = new THREE.LoadingManager(
    () => {
      state.ready = true;
    },
    undefined,
    () => {
      state.failed = true;
    },
  );
  const loader = new THREE.TextureLoader(manager);
  const loadTexture = (path: string, color = false) => {
    const texture = loader.load(getAssetUrl(path));
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.ClampToEdgeWrapping;
    texture.magFilter = THREE.LinearFilter;
    texture.minFilter = THREE.LinearMipmapLinearFilter;
    texture.generateMipmaps = true;
    texture.anisotropy = Math.min(8, maxAnisotropy);
    if (color) texture.colorSpace = THREE.SRGBColorSpace;
    return texture;
  };

  return {
    colorTexture: loadTexture("/planet/human/albedo.webp", true),
    emissiveTexture: loadTexture("/planet/human/emissive.webp", true),
    normalTexture: loadTexture("/planet/human/normal.webp"),
    roughnessTexture: loadTexture("/planet/human/roughness.webp"),
    state,
  };
}

export default function PrototypePlanet({ motionState }: PrototypePlanetProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [webglFailed, setWebglFailed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mobile = window.matchMedia("(max-width: 760px)").matches;
    const desktopOutcomes = window.matchMedia("(min-width: 1181px)").matches;
    const contextAttributes: WebGLContextAttributes = {
      alpha: true,
      antialias: !mobile,
      powerPreference: "high-performance",
    };
    const context = canvas.getContext("webgl2", contextAttributes)
      ?? canvas.getContext("webgl", contextAttributes);
    if (!context) {
      const fallbackFrame = window.requestAnimationFrame(() => setWebglFailed(true));
      return () => window.cancelAnimationFrame(fallbackFrame);
    }
    let renderer: THREE.WebGLRenderer;

    try {
      renderer = new THREE.WebGLRenderer({
        canvas,
        context,
        alpha: true,
        antialias: !mobile,
        powerPreference: "high-performance",
      });
    } catch {
      const fallbackFrame = window.requestAnimationFrame(() => setWebglFailed(true));
      return () => window.cancelAnimationFrame(fallbackFrame);
    }

    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, mobile ? 1.1 : 1.5));
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const onContextLost = (event: Event) => {
      event.preventDefault();
      setWebglFailed(true);
    };
    canvas.addEventListener("webglcontextlost", onContextLost);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(40, 1, 0.1, 100);
    camera.position.set(0, 0, mobile ? 6.85 : 6.25);
    const world = new THREE.Group();
    world.rotation.set(-0.07, 0.35, 0);
    scene.add(world);

    const globeGeometry = new THREE.SphereGeometry(1.58, mobile ? 48 : 84, mobile ? 48 : 84);
    const globeMaterial = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      uniforms: {
        uTime: { value: 0 },
        uHuman: { value: 0.28 },
        uUnified: { value: 0.35 },
        uEnergy: { value: 0.48 },
        uHumanFinish: { value: 0 },
        uVisibility: { value: 1 },
      },
      vertexShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float uTime;
        uniform float uHuman;
        uniform float uEnergy;
        void main() {
          vNormal = normalize(normalMatrix * normal);
          vPosition = position;
          float wave = sin(position.y * 5.5 + position.x * 3.0 + uTime * 0.62) * 0.018;
          float pulse = sin(uTime * 1.15 + position.z * 4.0) * 0.012 * uHuman;
          vec3 displaced = position + normal * (wave * uEnergy + pulse);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(displaced, 1.0);
        }
      `,
      fragmentShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float uTime;
        uniform float uHuman;
        uniform float uUnified;
        uniform float uEnergy;
        uniform float uHumanFinish;
        uniform float uVisibility;

        float humanHash(vec3 point) {
          point = fract(point * 0.1031);
          point += dot(point, point.yzx + 33.33);
          return fract((point.x + point.y) * point.z);
        }

        float humanNoise(vec3 point) {
          vec3 cell = floor(point);
          vec3 local = fract(point);
          local = local * local * (3.0 - 2.0 * local);

          float n000 = humanHash(cell + vec3(0.0, 0.0, 0.0));
          float n100 = humanHash(cell + vec3(1.0, 0.0, 0.0));
          float n010 = humanHash(cell + vec3(0.0, 1.0, 0.0));
          float n110 = humanHash(cell + vec3(1.0, 1.0, 0.0));
          float n001 = humanHash(cell + vec3(0.0, 0.0, 1.0));
          float n101 = humanHash(cell + vec3(1.0, 0.0, 1.0));
          float n011 = humanHash(cell + vec3(0.0, 1.0, 1.0));
          float n111 = humanHash(cell + vec3(1.0, 1.0, 1.0));

          float nearY0 = mix(mix(n000, n100, local.x), mix(n010, n110, local.x), local.y);
          float nearY1 = mix(mix(n001, n101, local.x), mix(n011, n111, local.x), local.y);
          return mix(nearY0, nearY1, local.z);
        }

        void main() {
          vec3 navy = vec3(0.027, 0.173, 0.365);
          vec3 blue = vec3(0.133, 0.471, 0.675);
          vec3 green = vec3(0.0, 0.624, 0.4);
          vec3 orange = vec3(0.965, 0.573, 0.118);
          vec3 sand = vec3(0.953, 0.859, 0.753);
          float latitude = smoothstep(-1.55, 1.55, vPosition.y);
          float flow = 0.5 + 0.5 * sin(vPosition.y * 5.2 + vPosition.x * 3.4 + uTime * (0.32 + uEnergy * 0.4));
          float contour = 0.5 + 0.5 * cos(length(vPosition.xy) * 8.0 - uTime * 0.4);
          vec3 precise = mix(navy, blue, latitude);
          precise = mix(precise, green, flow * 0.42 * uUnified);
          vec3 human = mix(orange, sand, latitude);
          human = mix(human, green, contour * 0.25 * uUnified);
          vec3 color = mix(precise, human, uHuman);
          vec3 normal = normalize(vNormal);
          float fresnel = pow(1.0 - abs(dot(normal, vec3(0.0, 0.0, 1.0))), 2.0);
          float alpha = 0.18 + fresnel * 0.68 + flow * 0.05;

          vec3 viewDirection = vec3(0.0, 0.0, 1.0);
          vec3 keyDirection = normalize(vec3(-0.58, 0.72, 0.64));
          vec3 fillDirection = normalize(vec3(0.7, -0.22, 0.5));
          float keyLight = smoothstep(-0.62, 0.9, dot(normal, keyDirection));
          float fillLight = max(dot(normal, fillDirection), 0.0);
          float crownLight = smoothstep(-0.3, 0.92, dot(normal, normalize(vec3(-0.2, 0.88, 0.5))));
          float specular = pow(max(dot(reflect(-keyDirection, normal), viewDirection), 0.0), 28.0);

          vec3 surfacePosition = normalize(vPosition);
          float macroSurface = humanNoise(surfacePosition * 2.15 + vec3(0.72, -0.18, 0.43));
          vec3 surfaceWarp = vec3(
            macroSurface * 1.55,
            -macroSurface * 1.05,
            macroSurface * 0.82
          );
          float middleSurface = humanNoise(surfacePosition * 4.35 + surfaceWarp);
          float fineSurface = humanNoise(surfacePosition * 9.2 + vec3(middleSurface * 1.15));
          float organicSurface = clamp(
            macroSurface * 0.58 + middleSurface * 0.3 + fineSurface * 0.12,
            0.0,
            1.0
          );
          float surfaceLift = smoothstep(0.3, 0.76, organicSurface);
          float luminousDepth = smoothstep(0.68, 0.92, middleSurface * 0.68 + fineSurface * 0.32)
            * surfaceLift;

          vec3 deepNavy = vec3(0.003, 0.025, 0.07);
          vec3 spatialBlue = vec3(0.01, 0.19, 0.32);
          vec3 atmosphericCyan = vec3(0.09, 0.52, 0.67);
          vec3 humanEmerald = vec3(0.0, 0.42, 0.3);
          vec3 humanDepth = mix(deepNavy, spatialBlue, keyLight * 0.82 + fillLight * 0.08);
          humanDepth = mix(humanDepth, atmosphericCyan, pow(keyLight, 1.7) * 0.36);
          humanDepth += humanEmerald * surfaceLift * (0.055 + keyLight * 0.105);
          humanDepth += atmosphericCyan * luminousDepth * keyLight * 0.12;
          humanDepth *= 0.72 + crownLight * 0.23;
          humanDepth += vec3(0.19, 0.7, 0.9) * specular * 0.2;
          humanDepth += vec3(0.09, 0.57, 0.7) * fresnel * (0.2 + keyLight * 0.34);

          color = mix(color, humanDepth, uHumanFinish * 0.985);
          float humanAlpha = 0.76 + keyLight * 0.08 + fresnel * 0.16 + luminousDepth * 0.025;
          alpha = mix(alpha, humanAlpha, uHumanFinish);
          gl_FragColor = vec4(color, alpha * uVisibility);
        }
      `,
    });
    const digitalGlobe = new THREE.Mesh(globeGeometry, globeMaterial);
    digitalGlobe.renderOrder = 3;
    world.add(digitalGlobe);

    const wireMaterial = new THREE.MeshBasicMaterial({
      color: 0x2278ac,
      transparent: true,
      opacity: 0.25,
      wireframe: true,
      depthWrite: false,
    });
    const wireGlobe = new THREE.Mesh(globeGeometry.clone(), wireMaterial);
    wireGlobe.scale.setScalar(1.01);
    world.add(wireGlobe);

    const digitalAtmosphereMaterial = new THREE.MeshBasicMaterial({
      color: 0x009f66,
      transparent: true,
      opacity: 0.1,
      side: THREE.BackSide,
      depthWrite: false,
    });
    world.add(new THREE.Mesh(
      new THREE.SphereGeometry(1.78, mobile ? 36 : 64, mobile ? 36 : 64),
      digitalAtmosphereMaterial,
    ));

    const humanDepthMaskMaterial = new THREE.MeshBasicMaterial({
      colorWrite: false,
      depthTest: true,
      depthWrite: true,
    });
    const humanDepthMask = new THREE.Mesh(globeGeometry.clone(), humanDepthMaskMaterial);
    humanDepthMask.scale.setScalar(0.985);
    humanDepthMask.renderOrder = 1;
    humanDepthMask.visible = false;
    world.add(humanDepthMask);

    const humanAtmosphereVertexShader = `
      varying vec3 vNormal;
      varying vec3 vPosition;
      void main() {
        vNormal = normalize(normalMatrix * normal);
        vPosition = position;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;
    const humanAtmosphereMaterial = new THREE.ShaderMaterial({
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        uStrength: { value: 0 },
      },
      vertexShader: humanAtmosphereVertexShader,
      fragmentShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float uStrength;
        void main() {
          vec3 normal = normalize(vNormal);
          float rim = pow(1.0 - abs(dot(normal, vec3(0.0, 0.0, 1.0))), 3.15);
          float tightRim = pow(rim, 3.8);
          float keySide = smoothstep(-0.3, 0.9, dot(normal, normalize(vec3(-0.68, 0.62, 0.5))));
          float vertical = smoothstep(-1.7, 1.7, vPosition.y);
          vec3 color = mix(vec3(0.0, 0.34, 0.27), vec3(0.24, 0.78, 0.94), keySide * 0.78 + vertical * 0.08);
          float alpha = (rim * (0.035 + keySide * 0.08) + tightRim * (0.13 + keySide * 0.11)) * uStrength;
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });
    const humanAtmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.625, mobile ? 36 : 64, mobile ? 36 : 64),
      humanAtmosphereMaterial,
    );
    humanAtmosphere.renderOrder = 2;
    world.add(humanAtmosphere);

    const humanHaloMaterial = new THREE.ShaderMaterial({
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.BackSide,
      transparent: true,
      uniforms: {
        uStrength: { value: 0 },
      },
      vertexShader: humanAtmosphereVertexShader,
      fragmentShader: `
        varying vec3 vNormal;
        varying vec3 vPosition;
        uniform float uStrength;
        void main() {
          vec3 normal = normalize(vNormal);
          float rim = pow(1.0 - abs(dot(normal, vec3(0.0, 0.0, 1.0))), 3.6);
          float keySide = smoothstep(-0.45, 0.88, dot(normal, normalize(vec3(-0.68, 0.62, 0.5))));
          vec3 color = mix(vec3(0.0, 0.25, 0.22), vec3(0.12, 0.58, 0.78), keySide);
          float alpha = rim * (0.012 + keySide * 0.026) * uStrength;
          gl_FragColor = vec4(color, alpha);
        }
      `,
    });
    const humanHalo = new THREE.Mesh(
      new THREE.SphereGeometry(1.72, mobile ? 36 : 64, mobile ? 36 : 64),
      humanHaloMaterial,
    );
    humanHalo.renderOrder = 1;
    world.add(humanHalo);

    const premiumHumanTextures = desktopOutcomes
      ? loadPremiumHumanSurfaceTextures(renderer.capabilities.getMaxAnisotropy())
      : null;
    const premiumHumanMaterial = new THREE.MeshPhysicalMaterial({
      clearcoat: 0.18,
      clearcoatRoughness: 0.48,
      color: 0xffffff,
      depthTest: true,
      depthWrite: false,
      dithering: true,
      emissive: 0xffffff,
      emissiveIntensity: 0.82,
      emissiveMap: premiumHumanTextures?.emissiveTexture ?? null,
      map: premiumHumanTextures?.colorTexture ?? null,
      metalness: 0.01,
      normalMap: premiumHumanTextures?.normalTexture ?? null,
      normalScale: new THREE.Vector2(0.24, 0.24),
      opacity: 0,
      roughness: 0.84,
      roughnessMap: premiumHumanTextures?.roughnessTexture ?? null,
      specularColor: 0xbceaf4,
      specularIntensity: 0.54,
      transparent: true,
    });
    const premiumHumanGlobe = new THREE.Mesh(globeGeometry.clone(), premiumHumanMaterial);
    premiumHumanGlobe.renderOrder = 4;
    premiumHumanGlobe.visible = false;
    world.add(premiumHumanGlobe);

    const earthTextures = createEarthTextures(mobile);
    const earthMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff,
      map: earthTextures?.surfaceTexture ?? null,
      metalness: 0.015,
      roughness: 0.63,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const earthGlobe = new THREE.Mesh(globeGeometry.clone(), earthMaterial);
    earthGlobe.renderOrder = 4;
    world.add(earthGlobe);

    const cloudMaterial = new THREE.MeshPhongMaterial({
      color: 0xffffff,
      map: earthTextures?.cloudTexture ?? null,
      blending: THREE.NormalBlending,
      transparent: true,
      opacity: 0,
      depthWrite: false,
    });
    const cloudGlobe = new THREE.Mesh(globeGeometry.clone(), cloudMaterial);
    cloudGlobe.scale.setScalar(1.012);
    cloudGlobe.renderOrder = 5;
    world.add(cloudGlobe);

    const earthAtmosphereMaterial = new THREE.MeshBasicMaterial({
      blending: THREE.AdditiveBlending,
      color: 0x6fcfff,
      transparent: true,
      opacity: 0,
      side: THREE.BackSide,
      depthWrite: false,
    });
    const earthAtmosphere = new THREE.Mesh(
      new THREE.SphereGeometry(1.69, mobile ? 36 : 64, mobile ? 36 : 64),
      earthAtmosphereMaterial,
    );
    earthAtmosphere.renderOrder = 1;
    world.add(earthAtmosphere);

    const environmentLight = new THREE.HemisphereLight(0xdaf3ff, 0x07162b, 1.05);
    scene.add(environmentLight);
    const earthKeyLight = new THREE.DirectionalLight(0xffffff, 2.15);
    earthKeyLight.position.set(-3.8, 2.5, 4.8);
    scene.add(earthKeyLight);
    const earthRimLight = new THREE.DirectionalLight(0x6fcfff, 1.05);
    earthRimLight.position.set(4.2, -1.4, -2.8);
    scene.add(earthRimLight);
    const humanKeyLight = new THREE.DirectionalLight(0xe6f8ff, 0);
    humanKeyLight.position.set(-4.8, 3.4, 5.6);
    scene.add(humanKeyLight);
    const humanFillLight = new THREE.DirectionalLight(0x0b4369, 0);
    humanFillLight.position.set(3.6, -2.2, 3.4);
    scene.add(humanFillLight);
    const humanRimLight = new THREE.DirectionalLight(0x35e6bf, 0);
    humanRimLight.position.set(4.8, 1.6, -4.2);
    scene.add(humanRimLight);

    const pointCount = mobile ? 620 : 1800;
    const positions = new Float32Array(pointCount * 3);
    let seed = 913;
    const random = () => {
      seed = (seed * 16807) % 2147483647;
      return (seed - 1) / 2147483646;
    };
    for (let index = 0; index < pointCount; index += 1) {
      const y = 1 - random() * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = random() * Math.PI * 2;
      const offset = 1.7 + (random() - 0.5) * 0.16;
      positions[index * 3] = Math.cos(theta) * radius * offset;
      positions[index * 3 + 1] = y * offset;
      positions[index * 3 + 2] = Math.sin(theta) * radius * offset;
    }
    const pointGeometry = new THREE.BufferGeometry();
    pointGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    const pointMaterial = new THREE.PointsMaterial({
      color: 0xd2e3f3,
      size: mobile ? 0.024 : 0.019,
      transparent: true,
      opacity: 0.68,
      depthWrite: false,
    });
    const pointCloud = new THREE.Points(pointGeometry, pointMaterial);
    world.add(pointCloud);

    const humanSurfacePointCount = desktopOutcomes ? 520 : 0;
    const humanSurfacePositions = new Float32Array(humanSurfacePointCount * 3);
    const humanSurfaceColors = new Float32Array(humanSurfacePointCount * 3);
    for (let index = 0; index < humanSurfacePointCount; index += 1) {
      const y = 1 - random() * 2;
      const radius = Math.sqrt(1 - y * y);
      const theta = random() * Math.PI * 2;
      const offset = 1.595 + random() * 0.028;
      const accentMix = random();
      humanSurfacePositions[index * 3] = Math.cos(theta) * radius * offset;
      humanSurfacePositions[index * 3 + 1] = y * offset;
      humanSurfacePositions[index * 3 + 2] = Math.sin(theta) * radius * offset;
      humanSurfaceColors[index * 3] = 0.18 - accentMix * 0.12;
      humanSurfaceColors[index * 3 + 1] = 0.64 + accentMix * 0.2;
      humanSurfaceColors[index * 3 + 2] = 0.82 - accentMix * 0.24;
    }
    const humanSurfaceGeometry = new THREE.BufferGeometry();
    humanSurfaceGeometry.setAttribute("position", new THREE.BufferAttribute(humanSurfacePositions, 3));
    humanSurfaceGeometry.setAttribute("color", new THREE.BufferAttribute(humanSurfaceColors, 3));
    const humanSurfaceMaterial = new THREE.PointsMaterial({
      blending: THREE.AdditiveBlending,
      depthTest: true,
      depthWrite: false,
      opacity: 0,
      size: 0.009,
      sizeAttenuation: true,
      toneMapped: false,
      transparent: true,
      vertexColors: true,
    });
    const humanSurfacePoints = new THREE.Points(humanSurfaceGeometry, humanSurfaceMaterial);
    humanSurfacePoints.renderOrder = 5;
    world.add(humanSurfacePoints);

    const orbitGroup = new THREE.Group();
    const orbitColors = [0x2278ac, 0x009f66, 0xf6921e, 0xd2e3f3];
    const precisionColor = new THREE.Color(0x6fcfff);
    const aiCapabilityColors = [
      new THREE.Color(0x2aa8e8),
      new THREE.Color(0x00d68a),
      new THREE.Color(0xf6921e),
    ];
    const judgmentForOrbit: Array<JudgmentForce | null> = [
      "nuance",
      "empathy",
      "accountability",
      null,
    ];
    const brandSignalForOrbit: Array<BrandSignal | null> = [
      "precision",
      "judgment",
      "trust",
      null,
    ];
    const outcomeSignalForOrbit: OutcomeSignal[] = [
      "clarity",
      "consistency",
      "confidence",
      "trust",
    ];
    const outcomeColors = desktopOutcomes
      ? [
          new THREE.Color(0xf6921e),
          new THREE.Color(0x009f66),
          new THREE.Color(0x2278ac),
          new THREE.Color(0x6fcfff),
        ]
      : [
          new THREE.Color(0x6fcfff),
          new THREE.Color(0x009f66),
          new THREE.Color(0xf6921e),
          new THREE.Color(0xf3dbc0),
        ];
    const questionColors: Record<FaqCategory, THREE.Color[]> = {
      about: [
        new THREE.Color(0x6fcfff),
        new THREE.Color(0x2278ac),
        new THREE.Color(0x6fcfff),
        new THREE.Color(0xd2e3f3),
      ],
      delivery: [
        new THREE.Color(0x009f66),
        new THREE.Color(0x00b879),
        new THREE.Color(0x009f66),
        new THREE.Color(0xd2e3f3),
      ],
      "ai-human": [
        new THREE.Color(0x6fcfff),
        new THREE.Color(0x00b879),
        new THREE.Color(0xf6921e),
        new THREE.Color(0xd2e3f3),
      ],
    };
    const industrySystems: Record<IndustrySignal, {
      primary: THREE.Color;
      secondary: THREE.Color;
      orbits: number[];
      nodes: number[];
    }> = {
      insurance: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0x2278ac),
        orbits: [2, 0],
        nodes: [0, 2, 4],
      },
      healthcare: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0xd2e3f3),
        orbits: [1, 3],
        nodes: [1, 3, 5],
      },
      financial: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0x6fcfff),
        orbits: [0, 3],
        nodes: [0, 3, 2],
      },
      technology: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0x2278ac),
        orbits: [0, 1],
        nodes: [0, 1, 3, 4],
      },
      consumer: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0xf6921e),
        orbits: [2, 1],
        nodes: [0, 1, 2, 3, 4, 5],
      },
      education: {
        primary: new THREE.Color(0x00b879),
        secondary: new THREE.Color(0x6fcfff),
        orbits: [0, 1],
        nodes: [5, 4, 3, 2],
      },
    };
    const orbitSystems = orbitColors.map((color, index) => {
      const radius = 2.03 + index * 0.14;
      const system = new THREE.Group();
      system.rotation.x = Math.PI * (0.28 + index * 0.17);
      system.rotation.y = Math.PI * (0.1 + index * 0.16);
      orbitGroup.add(system);

      const orbit = new THREE.Mesh(
        new THREE.TorusGeometry(radius, 0.006 + index * 0.0015, 8, mobile ? 110 : 190),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.34, depthWrite: false }),
      );
      system.add(orbit);

      const baseColor = new THREE.Color(color);
      const markerMaterial = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color,
        transparent: true,
        opacity: 0.72,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
      });
      const marker = new THREE.Mesh(
        new THREE.OctahedronGeometry(mobile ? 0.026 : 0.032, 1),
        markerMaterial,
      );
      marker.renderOrder = 8;

      const glowMaterial = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color,
        transparent: true,
        opacity: 0.18,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
      });
      const glow = new THREE.Mesh(
        new THREE.SphereGeometry(mobile ? 0.066 : 0.078, 14, 14),
        glowMaterial,
      );
      glow.renderOrder = 7;

      const traveler = new THREE.Group();
      traveler.add(glow, marker);
      system.add(traveler);

      const primaryStreakMaterial = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color,
        transparent: true,
        opacity: 0.08,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
      });
      const primaryStreak = new THREE.Mesh(
        new THREE.TorusGeometry(
          radius,
          mobile ? 0.008 : 0.0095,
          6,
          mobile ? 20 : 28,
          mobile ? 0.18 : 0.22,
        ),
        primaryStreakMaterial,
      );
      primaryStreak.renderOrder = 6;
      system.add(primaryStreak);

      const secondaryStreakMaterial = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color,
        transparent: true,
        opacity: 0.04,
        depthWrite: false,
        depthTest: false,
        toneMapped: false,
      });
      const secondaryStreak = new THREE.Mesh(
        new THREE.TorusGeometry(
          radius,
          mobile ? 0.0045 : 0.0055,
          5,
          mobile ? 13 : 18,
          mobile ? 0.1 : 0.13,
        ),
        secondaryStreakMaterial,
      );
      secondaryStreak.renderOrder = 5;
      system.add(secondaryStreak);

      const trail = Array.from({ length: mobile ? 3 : 4 }, (_, trailIndex) => {
        const material = new THREE.MeshBasicMaterial({
          blending: THREE.AdditiveBlending,
          color,
          transparent: true,
          opacity: 0.025,
          depthWrite: false,
          depthTest: false,
          toneMapped: false,
        });
        const bead = new THREE.Mesh(
          new THREE.SphereGeometry(
            (mobile ? 0.011 : 0.014) * (1 - trailIndex * 0.1),
            8,
            8,
          ),
          material,
        );
        bead.renderOrder = 6;
        system.add(bead);
        return { bead, material };
      });

      return {
        baseAngularSpeed: (Math.PI * 2) / [13.8, 16.2, 18.6, 21.4][index],
        baseColor,
        glowMaterial,
        marker,
        markerMaterial,
        orbit,
        phaseOffset: [0.12, 2.02, 3.66, 5.18][index],
        primaryStreak,
        primaryStreakMaterial,
        radius,
        secondaryStreak,
        secondaryStreakMaterial,
        system,
        trail,
        traveler,
      };
    });
    world.add(orbitGroup);

    const nodeLocations: Array<[number, number]> = [[41, -74], [19, -99], [51, 0], [25, 55], [1, 104], [-23, -46]];
    const nodes = nodeLocations.map(([latitude, longitude], index) => {
      const position = pointOnSphere(latitude, longitude, 1.71);
      const material = new THREE.MeshBasicMaterial({
        color: orbitColors[index % 3],
        transparent: true,
        opacity: 0.86,
      });
      const baseColor = new THREE.Color(orbitColors[index % 3]);
      const node = new THREE.Mesh(
        new THREE.SphereGeometry(mobile ? 0.04 : 0.048, 16, 16),
        material,
      );
      node.position.copy(position);
      world.add(node);
      const haloMaterial = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color: orbitColors[index % 3],
        transparent: true,
        opacity: 0,
        depthWrite: false,
        toneMapped: false,
      });
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(mobile ? 0.075 : 0.09, 16, 16),
        haloMaterial,
      );
      halo.position.copy(position);
      halo.renderOrder = 7;
      world.add(halo);
      return {
        baseColor,
        force: judgmentForOrbit[index % 3] as JudgmentForce,
        brandSignal: brandSignalForOrbit[index % 3] as BrandSignal,
        halo,
        haloMaterial,
        outcome: outcomeSignalForOrbit[index % 4],
        material,
        node,
        position,
      };
    });

    const industryCoreGroup = new THREE.Group();
    industryCoreGroup.position.z = 1.74;
    const industryCoreRings = [0.46, 0.58, 0.7].map((radius, index) => {
      const material = new THREE.MeshBasicMaterial({
        blending: THREE.AdditiveBlending,
        color: index === 1 ? 0x6fcfff : 0xf6921e,
        transparent: true,
        opacity: 0,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      });
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(radius, mobile ? 0.006 : 0.008, 6, mobile ? 64 : 96),
        material,
      );
      ring.renderOrder = 10;
      industryCoreGroup.add(ring);
      return { material, ring };
    });
    scene.add(industryCoreGroup);

    const connectionPositions: number[] = [];
    nodes.forEach(({ position }, index) => {
      const next = nodes[(index + 1) % nodes.length].position;
      connectionPositions.push(position.x, position.y, position.z, next.x, next.y, next.z);
    });
    const connectionGeometry = new THREE.BufferGeometry();
    connectionGeometry.setAttribute("position", new THREE.Float32BufferAttribute(connectionPositions, 3));
    const connectionMaterial = new THREE.LineBasicMaterial({
      color: 0xb6cdbf,
      transparent: true,
      opacity: 0.2,
    });
    world.add(new THREE.LineSegments(
      connectionGeometry,
      connectionMaterial,
    ));

    const pointer = { x: 0, y: 0 };
    const onPointerMove = (event: PointerEvent) => {
      pointer.x = (event.clientX / Math.max(1, window.innerWidth) - 0.5) * 2;
      pointer.y = -(event.clientY / Math.max(1, window.innerHeight) - 0.5) * 2;
    };
    window.addEventListener("pointermove", onPointerMove, { passive: true });

    const resize = () => {
      const bounds = canvas.getBoundingClientRect();
      renderer.setSize(Math.max(1, bounds.width), Math.max(1, bounds.height), false);
      camera.aspect = bounds.width / Math.max(1, bounds.height);
      camera.updateProjectionMatrix();
    };
    resize();
    window.addEventListener("resize", resize, { passive: true });

    const clock = new THREE.Clock();
    let motionTime = 0;
    let orbitTime = 0;
    let currentSpeed = 1;
    let currentAiWorld = 0;
    let currentHumanWorld = 0;
    let currentHumanFinish = 0;
    const wireBaseColor = new THREE.Color(0x2278ac);
    const humanWireColor = new THREE.Color(0x4dbda7);
    const aiCapabilityLevels = [0, 0, 0];
    const aiCapabilityPhases = [0, 0, 0];
    const outcomePhases = [0, 0, 0, 0];
    let aiCapabilitySequenceAge = 1;
    let lastAiCapabilityFocus: AICapabilitySignal | null = null;
    let outcomeSequenceAge = 1;
    let lastOutcomePresence = 0;
    let lastOutcomeSequence = -1;
    let industrySequenceAge = 1;
    let lastIndustryPresence = 0;
    let lastIndustrySequence = -1;
    let questionSequenceAge = 1;
    let lastQuestionPresence = 0;
    let lastQuestionSequence = -1;
    let frame = 0;
    let mobileCanvasVisible = true;
    let mobileVisibilityObserver: IntersectionObserver | null = null;
    const render = () => {
      frame = 0;
      const delta = Math.min(clock.getDelta(), 0.05);
      const requestedMotion = motionState.current;
      const reducedMotion = requestedMotion.mode === "reduced";
      const motionFactor = reducedMotion ? 0.22 : 1;
      const worldModeEase = 1 - Math.exp(-delta * (reducedMotion ? 18 : 3.1));
      currentAiWorld += (clamp(requestedMotion.aiWorld ?? 0) - currentAiWorld) * worldModeEase;
      currentHumanWorld += (clamp(requestedMotion.humanWorld ?? 0) - currentHumanWorld) * worldModeEase;
      const targetSpeed = reducedMotion
        ? Math.min(requestedMotion.speed, 1.08)
        : clamp(requestedMotion.speed, 1, 3.6);
      const speedEase = 1 - Math.exp(-delta * (targetSpeed > currentSpeed ? 3.8 : 4.8));
      currentSpeed += (targetSpeed - currentSpeed) * speedEase;
      const speedEnergy = reducedMotion
        ? clamp(requestedMotion.precision * 0.03)
        : clamp((currentSpeed - 1) / 2.6);
      const aiCapabilityEnergy = clamp(requestedMotion.precision ?? 0);
      const aiCapabilityFocus = requestedMotion.aiCapabilityFocus ?? null;
      const aiCapabilityIndex = aiCapabilityFocus === "speed"
        ? 0
        : aiCapabilityFocus === "pattern"
          ? 1
          : aiCapabilityFocus === "repeatability"
            ? 2
            : -1;
      if (aiCapabilityFocus && aiCapabilityFocus !== lastAiCapabilityFocus) {
        aiCapabilitySequenceAge = 0;
      }
      lastAiCapabilityFocus = aiCapabilityFocus;
      if (!aiCapabilityFocus || reducedMotion) {
        aiCapabilitySequenceAge = 1;
      } else {
        aiCapabilitySequenceAge = Math.min(1, aiCapabilitySequenceAge + delta / 0.72);
      }
      const aiCapabilityActivationPulse = aiCapabilitySequenceAge < 1
        ? Math.sin(aiCapabilitySequenceAge * Math.PI)
        : 0;
      const aiCapabilityEase = 1 - Math.exp(-delta * (reducedMotion ? 18 : 9));
      aiCapabilityLevels.forEach((level, index) => {
        const target = aiCapabilityEnergy > 0.02 && index === aiCapabilityIndex ? 1 : 0;
        aiCapabilityLevels[index] = level + (target - level) * aiCapabilityEase;
      });
      const brandSignalEnergy = clamp(requestedMotion.brandSignal ?? 0);
      const brandSignalFocus = requestedMotion.brandSignalFocus ?? null;
      const brandSignalSceneFade = 1 - brandSignalEnergy;
      const judgmentEnergy = clamp(requestedMotion.judgment ?? 0);
      const judgmentFocus = requestedMotion.judgmentFocus ?? null;
      const outcomeEnergy = clamp(requestedMotion.outcomes ?? 0);
      const outcomeFocus = requestedMotion.outcomeFocus ?? null;
      const warmOutcomeFocus = desktopOutcomes
        ? outcomeFocus === "clarity"
        : outcomeFocus === "trust";
      const outcomeSequence = requestedMotion.outcomeSequence ?? 0;
      if (
        outcomeEnergy > 0.08
        && (
          lastOutcomePresence <= 0.08
          || outcomeSequence !== lastOutcomeSequence
        )
      ) {
        outcomeSequenceAge = 0;
      }
      lastOutcomePresence = outcomeEnergy;
      lastOutcomeSequence = outcomeSequence;
      if (outcomeEnergy <= 0.08 || reducedMotion) {
        outcomeSequenceAge = 1;
      } else {
        outcomeSequenceAge = Math.min(1, outcomeSequenceAge + delta / 1.05);
      }
      const outcomeSequencePulse = outcomeSequenceAge < 1
        ? Math.sin(outcomeSequenceAge * Math.PI)
        : 0;
      const industryEnergy = clamp(requestedMotion.industries ?? 0);
      const industryFocus = requestedMotion.industryFocus ?? null;
      const industryPreview = requestedMotion.industryPreview ?? false;
      const industrySequence = requestedMotion.industrySequence ?? 0;
      const questionEnergy = clamp(requestedMotion.questions ?? 0);
      const questionCategory = requestedMotion.questionCategory ?? null;
      const questionPreview = requestedMotion.questionPreview ?? false;
      const questionSequence = requestedMotion.questionSequence ?? 0;
      const perspectiveEnergy = clamp(requestedMotion.perspectives ?? 0);
      const perspectiveFocus = requestedMotion.perspectiveFocus ?? null;
      const humanFinishTarget = desktopOutcomes && perspectiveFocus === "human" ? 1 : 0;
      const humanFinishEase = 1 - Math.exp(-delta * (reducedMotion ? 18 : 4.2));
      currentHumanFinish += (humanFinishTarget - currentHumanFinish) * humanFinishEase;
      const contactEnergy = clamp(requestedMotion.contact ?? 0);
      if (
        industryEnergy > 0.08
        && (
          lastIndustryPresence <= 0.08
          || industrySequence !== lastIndustrySequence
        )
      ) {
        industrySequenceAge = 0;
      }
      lastIndustryPresence = industryEnergy;
      lastIndustrySequence = industrySequence;
      if (industryEnergy <= 0.08 || reducedMotion || industryPreview) {
        industrySequenceAge = 1;
      } else {
        industrySequenceAge = Math.min(1, industrySequenceAge + delta / 0.82);
      }
      const industryConfig = industryFocus ? industrySystems[industryFocus] : null;
      const industrySequenceActive = industrySequenceAge < 1;
      const industrySequenceEnvelope = industrySequenceActive
        ? Math.sin(industrySequenceAge * Math.PI)
        : 0;
      const industryStrength = industryEnergy * (
        industryPreview
          ? 0.34
          : 0.22 + industrySequenceEnvelope * 0.78
      );
      if (
        questionEnergy > 0.08
        && (
          lastQuestionPresence <= 0.08
          || questionSequence !== lastQuestionSequence
        )
      ) {
        questionSequenceAge = 0;
      }
      lastQuestionPresence = questionEnergy;
      lastQuestionSequence = questionSequence;
      if (questionEnergy <= 0.08 || reducedMotion || questionPreview) {
        questionSequenceAge = 1;
      } else {
        questionSequenceAge = Math.min(1, questionSequenceAge + delta / 0.52);
      }
      const questionPulse = questionSequenceAge < 1
        ? Math.sin(questionSequenceAge * Math.PI)
        : 0;
      const questionMotionPace = 1 - questionEnergy * (reducedMotion ? 0.08 : 0.26);
      const contactMotionPace = 1 - contactEnergy * (reducedMotion ? 0.18 : 0.36);
      const orbitSpeed = (1 + speedEnergy * 4) * questionMotionPace * contactMotionPace;
      motionTime += delta * motionFactor * currentSpeed;
      orbitTime += delta * motionFactor * orbitSpeed;
      globeMaterial.uniforms.uTime.value = motionTime;
      globeMaterial.uniforms.uEnergy.value = 0.48
        + speedEnergy * 0.42
        + outcomeEnergy * outcomeSequencePulse * 0.08
        + currentAiWorld * 0.34;
      globeMaterial.uniforms.uHuman.value = 0.28
        + judgmentEnergy * (judgmentFocus ? 0.34 : 0.24)
        + outcomeEnergy * (warmOutcomeFocus ? 0.16 : 0.035)
        + industryEnergy * (
          industryFocus === "healthcare" || industryFocus === "education" ? 0.08 : 0.025
        )
        + perspectiveEnergy * (perspectiveFocus === "human" ? 0.24 : 0.055)
        + questionEnergy * (questionCategory === "ai-human" ? 0.16 : 0.035);
      globeMaterial.uniforms.uUnified.value = 0.35
        + judgmentEnergy * 0.16
        + outcomeEnergy * (0.08 + outcomeSequencePulse * 0.07)
        + industryEnergy * 0.055
        + perspectiveEnergy * 0.1
        + questionEnergy * 0.09;
      const premiumHumanPresence = Math.pow(currentHumanFinish, 0.78);
      const premiumHumanReady = Boolean(
        premiumHumanTextures?.state.ready && !premiumHumanTextures.state.failed,
      );
      const renderedPremiumHumanPresence = premiumHumanReady ? premiumHumanPresence : 0;
      globeMaterial.uniforms.uHumanFinish.value = currentHumanFinish;
      globeMaterial.uniforms.uVisibility.value = (1 - currentHumanWorld * 0.965)
        * (1 - renderedPremiumHumanPresence);
      premiumHumanMaterial.opacity = renderedPremiumHumanPresence;
      premiumHumanMaterial.depthWrite = renderedPremiumHumanPresence > 0.42;
      premiumHumanGlobe.visible = renderedPremiumHumanPresence > 0.005;
      const legacyHumanSurfacePresence = currentHumanWorld
        * (1 - renderedPremiumHumanPresence);
      earthMaterial.opacity = legacyHumanSurfacePresence;
      earthMaterial.depthWrite = legacyHumanSurfacePresence > 0.52;
      cloudMaterial.opacity = legacyHumanSurfacePresence * 0.52;
      earthAtmosphereMaterial.opacity = legacyHumanSurfacePresence * 0.19;
      digitalAtmosphereMaterial.opacity = 0.1
        * (1 - currentHumanWorld * 0.82)
        * (1 - currentHumanFinish * 0.96)
        + currentHumanFinish * 0.004;
      humanAtmosphereMaterial.uniforms.uStrength.value = renderedPremiumHumanPresence;
      humanHaloMaterial.uniforms.uStrength.value = renderedPremiumHumanPresence;
      humanAtmosphere.visible = currentHumanFinish > 0.005;
      humanHalo.visible = currentHumanFinish > 0.005;
      humanDepthMask.visible = currentHumanFinish > 0.015;
      humanSurfaceMaterial.opacity = currentHumanFinish * 0.055;
      humanSurfacePoints.visible = currentHumanFinish > 0.005;
      environmentLight.intensity = 1.05 * (1 - renderedPremiumHumanPresence * 0.64);
      earthKeyLight.intensity = 2.15 * (1 - renderedPremiumHumanPresence * 0.96);
      earthRimLight.intensity = 1.05 * (1 - renderedPremiumHumanPresence * 0.9);
      humanKeyLight.intensity = renderedPremiumHumanPresence * 2.42;
      humanFillLight.intensity = renderedPremiumHumanPresence * 0.08;
      humanRimLight.intensity = renderedPremiumHumanPresence * 1.68;
      world.rotation.y += delta * motionFactor * (0.11 + speedEnergy * 0.72 + currentAiWorld * 0.08)
        * (1 - currentHumanWorld * 0.56) * questionMotionPace * contactMotionPace;
      world.rotation.x += (pointer.y * 0.045 - 0.07 - world.rotation.x) * 0.018 * motionFactor;
      cloudGlobe.rotation.y += delta * motionFactor * 0.014 * currentHumanWorld;
      pointCloud.rotation.y = -orbitTime * (0.025 + speedEnergy * 0.035);
      pointMaterial.opacity = (0.64 + speedEnergy * 0.24)
        * (1 - contactEnergy * 0.52)
        * (1 - brandSignalEnergy * 0.28)
        * (1 - currentHumanWorld * 0.2)
        * (1 - currentHumanFinish * 0.84);
      wireMaterial.color.copy(wireBaseColor).lerp(humanWireColor, currentHumanFinish * 0.72);
      wireGlobe.renderOrder = currentHumanFinish > 0.02 ? 5 : 0;
      wireMaterial.opacity = 0.25
        * (1 - brandSignalEnergy * 0.12)
        * (1 - currentHumanWorld * 0.72)
        * (1 - currentHumanFinish * 0.925);
      orbitGroup.rotation.z = orbitTime * (0.016 + speedEnergy * 0.022);
      orbitGroup.rotation.y = orbitTime * -(0.01 + speedEnergy * 0.016);
      orbitSystems.forEach((orbitSystem, index) => {
        const {
          baseAngularSpeed,
          baseColor,
          glowMaterial,
          marker,
          markerMaterial,
          orbit,
          phaseOffset,
          primaryStreak,
          primaryStreakMaterial,
          radius,
          secondaryStreak,
          secondaryStreakMaterial,
          system,
          trail,
          traveler,
        } = orbitSystem;
        const humanDepthOcclusion = currentHumanFinish > 0.08;
        markerMaterial.depthTest = humanDepthOcclusion;
        glowMaterial.depthTest = humanDepthOcclusion;
        primaryStreakMaterial.depthTest = humanDepthOcclusion;
        secondaryStreakMaterial.depthTest = humanDepthOcclusion;
        trail.forEach(({ material }) => {
          material.depthTest = humanDepthOcclusion;
        });
        const judgmentForce = judgmentForOrbit[index];
        const brandSignal = brandSignalForOrbit[index];
        const brandSignalBase = brandSignal ? brandSignalEnergy : brandSignalEnergy * 0.12;
        const brandSignalEmphasis = brandSignal && brandSignalFocus === brandSignal
          ? brandSignalEnergy
          : 0;
        const brandSignalDim = brandSignalFocus && brandSignal !== brandSignalFocus
          ? 0.48
          : 1;
        const judgmentBase = judgmentForce ? judgmentEnergy : judgmentEnergy * 0.12;
        const judgmentResponseWeight = judgmentForce === "nuance"
          ? 1.12
          : judgmentForce === "accountability"
            ? 0.82
            : 0.96;
        const judgmentEmphasis = judgmentForce && judgmentFocus === judgmentForce
          ? judgmentEnergy * judgmentResponseWeight
          : 0;
        const judgmentDim = judgmentFocus && judgmentForce !== judgmentFocus
          ? (judgmentFocus === "nuance" ? 0.24 : 0.32)
          : 1;
        const outcomeSignal = outcomeSignalForOrbit[index];
        const outcomeBase = outcomeSignal ? outcomeEnergy : outcomeEnergy * 0.12;
        const outcomeEmphasis = outcomeSignal && outcomeFocus === outcomeSignal
          ? outcomeEnergy
          : 0;
        const outcomeDim = outcomeFocus && outcomeSignal !== outcomeFocus
          ? 0.42
          : 1;
        const outcomeMotionWeight = [0.78, 0.46, 0.62, 0.42][index] ?? 0.5;
        const outcomeMotionPulse = outcomeEmphasis * outcomeSequencePulse * outcomeMotionWeight;
        const industryOrbitPosition = industryConfig?.orbits.indexOf(index) ?? -1;
        const industryOrbitActive = industryOrbitPosition >= 0;
        const technologyRoute = industryFocus === "technology" && industrySequenceActive
          ? (
              industrySequenceAge < 0.48
                ? industryOrbitPosition === 0
                : industryOrbitPosition === 1
            )
          : industryOrbitActive;
        const industryOrbitEmphasis = technologyRoute
          ? industryStrength * (industryOrbitPosition === 0 ? 1 : 0.88)
          : 0;
        const industryDim = industryEnergy > 0.02 && !industryOrbitActive
          ? (industryPreview ? 0.82 : 0.64)
          : 1;
        const industryOrbitColor = industryConfig
          ? (industryOrbitPosition === 1 ? industryConfig.secondary : industryConfig.primary)
          : baseColor;
        const perspectiveOrbit = index === 0 ? "ai" : index === 1 ? "human" : null;
        const perspectiveBase = perspectiveOrbit ? perspectiveEnergy * 0.18 : 0;
        const perspectiveEmphasis = perspectiveOrbit && perspectiveFocus === perspectiveOrbit
          ? perspectiveEnergy
          : 0;
        const perspectiveDim = perspectiveFocus && perspectiveOrbit !== perspectiveFocus
          ? (perspectiveOrbit ? 0.42 : 0.68)
          : 1;
        const questionOrbitActive = questionCategory === "about"
          ? index === 0
          : questionCategory === "delivery"
            ? index === 1
            : questionCategory === "ai-human"
              ? index <= 2
              : false;
        const questionOrbitBase = questionOrbitActive
          ? questionEnergy * (questionPreview ? 0.14 : 0.24)
          : 0;
        const questionOrbitPulse = questionOrbitActive
          ? questionEnergy * questionPulse * 0.42
          : 0;
        const questionOrbitEmphasis = questionOrbitBase + questionOrbitPulse;
        const questionOrbitColor = questionCategory
          ? questionColors[questionCategory][index]
          : baseColor;
        const questionOrbitDim = questionEnergy > 0.02 && !questionOrbitActive ? 0.66 : 1;
        const aiCapabilityOrbit = index === 0
          ? "speed"
          : index === 1
            ? "pattern"
            : index === 2
              ? "repeatability"
              : null;
        const aiCapabilityLevel = index < 3 ? aiCapabilityLevels[index] : 0;
        const aiCapabilityEmphasis = aiCapabilityEnergy * aiCapabilityLevel;
        const aiCapabilityPulse = aiCapabilityEmphasis * aiCapabilityActivationPulse;
        const aiCapabilityResponseWeight = index === 0 ? 0.92 : index === 1 ? 0.86 : index === 2 ? 0.8 : 1;
        const aiCapabilityPulseWeight = index === 0 ? 0.82 : index === 1 ? 0.66 : index === 2 ? 0.62 : 1;
        const aiCapabilityVisualEmphasis = aiCapabilityEmphasis * aiCapabilityResponseWeight;
        const aiCapabilityVisualPulse = aiCapabilityPulse * aiCapabilityPulseWeight;
        const aiCapabilityDim = aiCapabilityFocus && aiCapabilityOrbit !== aiCapabilityFocus
          ? (aiCapabilityOrbit ? 0.54 : 0.76)
          : 1;
        const aiCapabilityColor = index < 3 ? aiCapabilityColors[index] : baseColor;
        const precisionWash = speedEnergy * (aiCapabilityFocus ? 0.28 : 1);
        const combinedDim = judgmentDim
          * brandSignalDim
          * outcomeDim
          * industryDim
          * perspectiveDim
          * questionOrbitDim
          * aiCapabilityDim;
        if (index < 3) {
          const capabilityPace = index === 0 ? 1.36 : index === 1 ? 0.24 : 0.34;
          aiCapabilityPhases[index] += delta * motionFactor * aiCapabilityEmphasis * capabilityPace;
        }
        if (outcomeEmphasis > 0.02) {
          const outcomePace = [1.18, 0.34, 0.56, 0.22][index] ?? 0.4;
          outcomePhases[index] += delta * motionFactor * outcomeEmphasis
            * outcomePace * (1 + outcomeMotionPulse * 0.48);
        }
        const angle = orbitTime * baseAngularSpeed
          + phaseOffset
          + (aiCapabilityPhases[index] ?? 0)
          + (outcomePhases[index] ?? 0);
        const trailStep = 0.022 + speedEnergy * 0.038 + aiCapabilityEmphasis * 0.016;
        const markerScale = (mobile ? 0.9 : 1)
          * (
            0.86
            + speedEnergy * 0.2
            + judgmentBase * 0.08
            + judgmentEmphasis * 0.2
            + outcomeBase * 0.06
            + outcomeEmphasis * 0.18
            + outcomeMotionPulse * 0.12
            + industryOrbitEmphasis * 0.22
            + perspectiveBase * 0.12
            + perspectiveEmphasis * 0.28
            + questionOrbitEmphasis * 0.42
            + aiCapabilityVisualEmphasis * 0.34
            + aiCapabilityVisualPulse * 0.12
          );

        system.rotation.z = orbitTime * (0.007 + index * 0.002) * (index % 2 ? -1 : 1);
        traveler.position.set(Math.cos(angle) * radius, Math.sin(angle) * radius, 0);
        traveler.scale.setScalar(markerScale);
        marker.rotation.z = angle * 0.48;
        markerMaterial.color
          .copy(baseColor)
          .lerp(precisionColor, precisionWash * 0.46)
          .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.92)
          .lerp(outcomeColors[index], outcomeEmphasis * 0.78)
          .lerp(industryOrbitColor, industryOrbitEmphasis * 0.72)
          .lerp(questionOrbitColor, questionOrbitEmphasis * 0.86);
        markerMaterial.opacity = (
          0.58
          + speedEnergy * 0.32
          + brandSignalBase * 0.09
          + brandSignalEmphasis * 0.25
          + judgmentBase * 0.12
          + judgmentEmphasis * 0.28
          + outcomeBase * 0.08
          + outcomeEmphasis * 0.32
          + outcomeMotionPulse * 0.14
          + industryOrbitEmphasis * 0.34
          + perspectiveBase * 0.12
          + perspectiveEmphasis * 0.3
          + questionOrbitEmphasis * 0.38
          + aiCapabilityVisualEmphasis * 0.34
          + aiCapabilityVisualPulse * 0.12
        ) * combinedDim * (1 - contactEnergy * 0.42) * brandSignalSceneFade;
        glowMaterial.color
          .copy(baseColor)
          .lerp(precisionColor, precisionWash * 0.58)
          .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.96)
          .lerp(outcomeColors[index], outcomeEmphasis * 0.82)
          .lerp(industryOrbitColor, industryOrbitEmphasis * 0.78)
          .lerp(questionOrbitColor, questionOrbitEmphasis * 0.9);
        glowMaterial.opacity = (
          0.045
          + speedEnergy * 0.22
          + brandSignalBase * 0.08
          + brandSignalEmphasis * 0.24
          + judgmentBase * 0.08
          + judgmentEmphasis * 0.3
          + outcomeBase * 0.065
          + outcomeEmphasis * 0.3
          + outcomeMotionPulse * 0.17
          + industryOrbitEmphasis * 0.36
          + perspectiveBase * 0.1
          + perspectiveEmphasis * 0.32
          + questionOrbitEmphasis * 0.44
          + aiCapabilityVisualEmphasis * 0.42
          + aiCapabilityVisualPulse * 0.18
        ) * combinedDim * (1 - contactEnergy * 0.48) * brandSignalSceneFade;
        (orbit.material as THREE.MeshBasicMaterial).color
          .copy(baseColor)
          .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.9)
          .lerp(outcomeColors[index], outcomeEmphasis * 0.84)
          .lerp(questionOrbitColor, questionOrbitEmphasis * 0.82);
        (orbit.material as THREE.MeshBasicMaterial).opacity = (
          0.26
          + speedEnergy * 0.16
          + brandSignalBase * 0.08
          + brandSignalEmphasis * 0.24
          + judgmentBase * 0.1
          + judgmentEmphasis * 0.28
          + outcomeBase * 0.075
          + outcomeEmphasis * 0.28
          + outcomeMotionPulse * 0.12
          + industryOrbitEmphasis * 0.34
          + perspectiveBase * 0.1
          + perspectiveEmphasis * 0.36
          + questionOrbitEmphasis * 0.42
          + aiCapabilityVisualEmphasis * 0.4
          + aiCapabilityVisualPulse * 0.1
        ) * combinedDim * (1 - contactEnergy * 0.16) * brandSignalSceneFade;

        primaryStreak.rotation.z = angle - (mobile ? 0.18 : 0.22);
        primaryStreakMaterial.color
          .copy(baseColor)
          .lerp(precisionColor, precisionWash * 0.58)
          .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.96)
          .lerp(outcomeColors[index], outcomeEmphasis * 0.88)
          .lerp(industryOrbitColor, industryOrbitEmphasis * 0.72)
          .lerp(questionOrbitColor, questionOrbitEmphasis * 0.88);
        primaryStreakMaterial.opacity = Math.min((
          0.035
          + speedEnergy * 0.48
          + brandSignalBase * 0.035
          + brandSignalEmphasis * 0.2
          + judgmentBase * 0.045
          + judgmentEmphasis * 0.24
          + outcomeBase * 0.035
          + outcomeEmphasis * 0.16
          + outcomeMotionPulse * 0.15
          + industryOrbitEmphasis * 0.26
          + perspectiveBase * 0.055
          + perspectiveEmphasis * 0.24
          + questionOrbitEmphasis * 0.32
          + aiCapabilityVisualEmphasis * 0.46
          + aiCapabilityVisualPulse * 0.16
        ) * combinedDim * (1 - contactEnergy * 0.42) * brandSignalSceneFade,
        aiCapabilityFocus === "speed" && aiCapabilityOrbit === "speed" ? 0.56 : 1);
        secondaryStreak.rotation.z = angle - (mobile ? 0.35 : 0.42);
        secondaryStreakMaterial.color
          .copy(baseColor)
          .lerp(precisionColor, precisionWash * 0.46)
          .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.9)
          .lerp(outcomeColors[index], outcomeEmphasis * 0.78)
          .lerp(industryOrbitColor, industryOrbitEmphasis * 0.66)
          .lerp(questionOrbitColor, questionOrbitEmphasis * 0.82);
        secondaryStreakMaterial.opacity = Math.min((
          0.015
          + speedEnergy * 0.2
          + brandSignalBase * 0.02
          + brandSignalEmphasis * 0.1
          + judgmentBase * 0.025
          + judgmentEmphasis * 0.12
          + outcomeBase * 0.02
          + outcomeEmphasis * 0.08
          + outcomeMotionPulse * 0.07
          + industryOrbitEmphasis * 0.14
          + perspectiveBase * 0.035
          + perspectiveEmphasis * 0.14
          + questionOrbitEmphasis * 0.2
          + aiCapabilityVisualEmphasis * 0.24
          + aiCapabilityVisualPulse * 0.08
        ) * combinedDim * (1 - contactEnergy * 0.42) * brandSignalSceneFade,
        aiCapabilityFocus === "speed" && aiCapabilityOrbit === "speed" ? 0.3 : 1);

        trail.forEach(({ bead, material }, trailIndex) => {
          const trailAngle = angle - trailStep * (trailIndex + 1);
          const fade = 1 - (trailIndex + 1) / (trail.length + 1);
          bead.position.set(
            Math.cos(trailAngle) * radius,
            Math.sin(trailAngle) * radius,
            0,
          );
          bead.scale.setScalar(0.72 + speedEnergy * 0.24);
          material.color
            .copy(baseColor)
            .lerp(precisionColor, precisionWash * 0.52)
            .lerp(aiCapabilityColor, aiCapabilityEmphasis * 0.92)
            .lerp(outcomeColors[index], outcomeEmphasis * 0.74)
            .lerp(industryOrbitColor, industryOrbitEmphasis * 0.7)
            .lerp(questionOrbitColor, questionOrbitEmphasis * 0.84);
          material.opacity = (
            0.01
            + speedEnergy * 0.24
            + brandSignalBase * 0.014
            + brandSignalEmphasis * 0.075
            + judgmentBase * 0.018
            + judgmentEmphasis * 0.1
            + outcomeBase * 0.014
            + outcomeEmphasis * 0.06
            + outcomeMotionPulse * 0.055
            + industryOrbitEmphasis * 0.09
            + perspectiveBase * 0.025
            + perspectiveEmphasis * 0.1
            + questionOrbitEmphasis * 0.14
            + aiCapabilityVisualEmphasis * 0.2
            + aiCapabilityVisualPulse * 0.06
          ) * Math.pow(fade, 1.35) * combinedDim * brandSignalSceneFade;
        });
      });
      nodes.forEach(({ baseColor, brandSignal, force, halo, haloMaterial, material, node, outcome }, index) => {
        const brandSignalEmphasis = brandSignalFocus === brandSignal ? brandSignalEnergy : 0;
        const brandSignalDim = brandSignalFocus && brandSignalFocus !== brandSignal ? 0.62 : 1;
        const judgmentResponseWeight = force === "nuance"
          ? 1.12
          : force === "accountability"
            ? 0.82
            : 0.96;
        const judgmentEmphasis = judgmentFocus === force
          ? judgmentEnergy * judgmentResponseWeight
          : 0;
        const judgmentDim = judgmentFocus && judgmentFocus !== force
          ? (judgmentFocus === "nuance" ? 0.42 : 0.52)
          : 1;
        const outcomeEmphasis = outcomeFocus === outcome ? outcomeEnergy : 0;
        const outcomeDim = outcomeFocus && outcomeFocus !== outcome ? 0.52 : 1;
        const outcomeNodeIndex = Math.max(0, outcomeSignalForOrbit.indexOf(outcome));
        const outcomeNodeColor = outcomeColors[outcomeNodeIndex] ?? baseColor;
        const outcomeNodePulse = outcomeEmphasis * outcomeSequencePulse
          * ([0.72, 0.48, 0.6, 0.42][outcomeNodeIndex] ?? 0.5);
        const perspectiveNode = index === 0 ? "ai" : index === 1 ? "human" : null;
        const perspectiveNodeEmphasis = perspectiveNode && perspectiveFocus === perspectiveNode
          ? perspectiveEnergy
          : 0;
        const perspectiveNodeColor = perspectiveNode === "ai" ? precisionColor : baseColor;
        const perspectiveNodeDim = perspectiveFocus && perspectiveNode !== perspectiveFocus
          ? (perspectiveNode ? 0.52 : 0.76)
          : 1;
        const questionNodeActive = questionCategory === "about"
          ? index === 0 || index === 2 || index === 4
          : questionCategory === "delivery"
            ? true
            : questionCategory === "ai-human"
              ? index <= 2
              : false;
        const questionNodeBase = questionNodeActive
          ? questionEnergy * (questionPreview ? 0.1 : 0.2)
          : 0;
        const questionNodePulse = questionNodeActive
          ? questionEnergy * questionPulse * (questionCategory === "delivery" ? 0.28 : 0.46)
          : 0;
        const questionNodeEmphasis = questionNodeBase + questionNodePulse;
        const questionNodeColor = questionCategory
          ? questionColors[questionCategory][index % 4]
          : baseColor;
        const questionNodeDim = questionEnergy > 0.02 && !questionNodeActive ? 0.68 : 1;
        const aiCapabilityNodeIndex = index % 3;
        const aiCapabilityNodeLevel = aiCapabilityLevels[aiCapabilityNodeIndex];
        const aiCapabilityNodeEmphasis = aiCapabilityEnergy * aiCapabilityNodeLevel;
        const aiCapabilityNodeDim = aiCapabilityFocus && aiCapabilityNodeIndex !== aiCapabilityIndex
          ? 0.58
          : 1;
        const aiCapabilityNodeColor = aiCapabilityColors[aiCapabilityNodeIndex];
        const aiCapabilityNodeSequenceRaw = reducedMotion
          || aiCapabilityFocus !== "pattern"
          || aiCapabilityNodeIndex !== 1
          ? 1
          : clamp((aiCapabilitySequenceAge - (index === 1 ? 0 : 0.18)) / (index === 1 ? 0.34 : 0.4));
        const aiCapabilityNodeSequence = aiCapabilityNodeSequenceRaw
          * aiCapabilityNodeSequenceRaw
          * (3 - 2 * aiCapabilityNodeSequenceRaw);
        const aiCapabilityNodeWeight = aiCapabilityNodeIndex === 0
          ? (index === 0 ? 0.9 : 0.6)
          : aiCapabilityNodeIndex === 1
            ? (index === 1 ? 0.74 : 0.48)
            : (index === 2 ? 0.76 : 0.52);
        const aiCapabilityNodeVisualEmphasis = aiCapabilityNodeEmphasis
          * aiCapabilityNodeWeight
          * aiCapabilityNodeSequence;
        const aiCapabilityNodeRhythm = reducedMotion
          ? 0.72
          : aiCapabilityFocus === "pattern" && aiCapabilityNodeIndex === 1
            ? 0.88 + Math.sin(orbitTime * 4.2 + index) * 0.12
            : aiCapabilityFocus === "repeatability" && aiCapabilityNodeIndex === 2
              ? 0.92 + Math.sin(orbitTime * 2.5 + index) * 0.08
              : 1;
        const industryNodePosition = industryConfig?.nodes.indexOf(index) ?? -1;
        const industryNodeActive = industryNodePosition >= 0;
        const industryNodeBase = industryNodeActive
          ? industryEnergy * (industryPreview ? 0.12 : 0.18)
          : 0;
        const nodeCount = Math.max(1, industryConfig?.nodes.length ?? 1);
        const nodeStart = industryNodePosition < 0
          ? 1
          : industryFocus === "consumer"
            ? 0.2
            : 0.08 + (industryNodePosition / nodeCount) * 0.58;
        const nodeProgressRaw = clamp((industrySequenceAge - nodeStart) / (
          industryFocus === "consumer" ? 0.22 : 0.26
        ));
        const nodeProgress = nodeProgressRaw * nodeProgressRaw * (3 - 2 * nodeProgressRaw);
        const industryNodeSequence = industryNodeActive && industrySequenceActive
          ? industryEnergy * nodeProgress * (
              industryFocus === "financial"
                ? 0.62 + industrySequenceEnvelope * 0.38
                : 0.72 + industrySequenceEnvelope * 0.28
            )
          : 0;
        const industryNodeColor = industryConfig
          ? (industryNodePosition % 2 === 1 ? industryConfig.secondary : industryConfig.primary)
          : baseColor;
        const industryNodeDim = industryEnergy > 0.02 && !industryNodeActive
          ? (industryPreview ? 0.82 : 0.66)
          : 1;
        const nodePulse = 1 + Math.sin(orbitTime * (1.4 + speedEnergy * 2.8) + index) * 0.18;
        node.scale.setScalar(nodePulse * (
          1
          + speedEnergy * 0.7
          + brandSignalEnergy * 0.06
          + brandSignalEmphasis * 0.3
          + judgmentEnergy * 0.08
          + judgmentEmphasis * 0.34
          + outcomeEnergy * 0.05
          + outcomeEmphasis * 0.38
          + outcomeNodePulse * 0.16
          + industryNodeBase * 0.38
          + industryNodeSequence * 0.78
          + perspectiveEnergy * 0.06
          + perspectiveNodeEmphasis * 0.42
          + questionNodeEmphasis * 0.54
          + aiCapabilityNodeVisualEmphasis * (0.36 + aiCapabilityNodeRhythm * 0.12)
        ));
        material.color
          .copy(baseColor)
          .lerp(aiCapabilityNodeColor, aiCapabilityNodeEmphasis * 0.94)
          .lerp(outcomeNodeColor, outcomeEmphasis * 0.82)
          .lerp(industryNodeColor, clamp(industryNodeBase * 1.8 + industryNodeSequence * 0.72))
          .lerp(questionNodeColor, clamp(questionNodeEmphasis * 1.25));
        material.opacity = (
          0.82
          + brandSignalEnergy * 0.06
          + brandSignalEmphasis * 0.1
          + judgmentEnergy * 0.08
          + judgmentEmphasis * 0.1
          + outcomeEnergy * 0.05
          + outcomeEmphasis * 0.12
          + outcomeNodePulse * 0.12
          + industryNodeBase * 0.1
          + industryNodeSequence * 0.16
          + perspectiveNodeEmphasis * 0.12
          + questionNodeEmphasis * 0.16
          + aiCapabilityNodeVisualEmphasis * 0.16
        ) * brandSignalDim
          * judgmentDim
          * outcomeDim
          * industryNodeDim
          * perspectiveNodeDim
          * questionNodeDim
          * aiCapabilityNodeDim
          * (1 - contactEnergy * 0.48)
          * brandSignalSceneFade;
        haloMaterial.color
          .copy(industryNodeColor)
          .lerp(aiCapabilityNodeColor, aiCapabilityNodeEmphasis * 0.94)
          .lerp(outcomeNodeColor, outcomeEmphasis * 0.78)
          .lerp(perspectiveNodeColor, perspectiveNodeEmphasis * 0.82)
          .lerp(questionNodeColor, questionNodeEmphasis * 0.9);
        haloMaterial.opacity = (
          industryNodeBase * 0.22
          + industryNodeSequence * 0.42
          + outcomeEmphasis * 0.16
          + outcomeNodePulse * 0.22
          + judgmentEmphasis * (force === "nuance" ? 0.3 : 0.22)
          + perspectiveNodeEmphasis * 0.36
          + questionNodeEmphasis * 0.42
          + aiCapabilityNodeVisualEmphasis * (0.24 + aiCapabilityNodeRhythm * 0.12)
        ) * judgmentDim * (reducedMotion ? 0.48 : 1);
        halo.scale.setScalar(
          0.8
          + industryNodeBase * 0.6
          + industryNodeSequence * 1.25
          + outcomeEmphasis * 0.34
          + outcomeNodePulse * 0.48
          + judgmentEmphasis * (force === "nuance" ? 0.62 : 0.48)
          + perspectiveNodeEmphasis * 0.8
          + questionNodeEmphasis * 0.9
          + aiCapabilityNodeVisualEmphasis * (0.52 + aiCapabilityNodeRhythm * 0.18),
        );
      });
      connectionMaterial.opacity = (0.2 * (1 - outcomeEnergy * 0.72)
        + judgmentEnergy * 0.1
        + outcomeEnergy * (warmOutcomeFocus ? 0.045 : 0.012)
        + industryEnergy * (
          industryFocus === "healthcare" || industryFocus === "consumer" ? 0.09 : 0.025
        )
        + perspectiveEnergy * (perspectiveFocus ? 0.08 : 0.035)
        + questionEnergy * (questionCategory === "ai-human" ? 0.1 : 0.035))
        * (1 - contactEnergy * 0.42)
        * (1 - brandSignalEnergy * 0.62);
      industryCoreRings.forEach(({ material, ring }, index) => {
        const financialRing = industryFocus === "financial"
          ? industryEnergy * (0.16 + industryStrength * (0.34 - index * 0.055))
          : 0;
        const insuranceConfirmation = industryFocus === "insurance" && industrySequenceActive
          ? industryEnergy * clamp((industrySequenceAge - 0.62) / 0.25) * (0.34 - index * 0.055)
          : 0;
        const healthcareConfirmation = industryFocus === "healthcare" && industrySequenceActive
          ? industryEnergy * clamp((industrySequenceAge - 0.68) / 0.2) * (0.27 - index * 0.045)
          : 0;
        const educationConfirmation = industryFocus === "education" && industrySequenceActive
          ? industryEnergy * clamp((industrySequenceAge - 0.7) / 0.2) * (0.21 - index * 0.035)
          : 0;
        const ringOpacity = financialRing
          + insuranceConfirmation
          + healthcareConfirmation
          + educationConfirmation;
        const ringColor = industryConfig
          ? (index === 1 ? industryConfig.secondary : industryConfig.primary)
          : outcomeColors[index];
        material.color.copy(ringColor);
        material.opacity = reducedMotion ? ringOpacity * 0.3 : ringOpacity;
        ring.scale.setScalar(
          0.94
          + index * 0.018
          + industrySequenceEnvelope * 0.055,
        );
        ring.rotation.z = orbitTime * (0.035 + index * 0.018) * (index % 2 ? -1 : 1);
      });
      renderer.render(scene, camera);
      if (!mobile || mobileCanvasVisible) {
        frame = window.requestAnimationFrame(render);
      }
    };

    if (mobile && "IntersectionObserver" in window) {
      mobileVisibilityObserver = new IntersectionObserver(([entry]) => {
        mobileCanvasVisible = entry?.isIntersecting ?? true;
        if (mobileCanvasVisible && !frame) {
          frame = window.requestAnimationFrame(render);
        }
      }, { rootMargin: "120px 0px" });
      mobileVisibilityObserver.observe(canvas);
    }
    render();

    return () => {
      window.cancelAnimationFrame(frame);
      mobileVisibilityObserver?.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("resize", resize);
      canvas.removeEventListener("webglcontextlost", onContextLost);
      premiumHumanTextures?.colorTexture.dispose();
      premiumHumanTextures?.emissiveTexture.dispose();
      premiumHumanTextures?.normalTexture.dispose();
      premiumHumanTextures?.roughnessTexture.dispose();
      earthTextures?.surfaceTexture.dispose();
      earthTextures?.cloudTexture.dispose();
      scene.traverse((object) => {
        if (object instanceof THREE.Mesh || object instanceof THREE.Points || object instanceof THREE.LineSegments) {
          object.geometry?.dispose();
          const material = object.material;
          if (Array.isArray(material)) material.forEach((item) => item.dispose());
          else material?.dispose();
        }
      });
      renderer.dispose();
    };
  }, [motionState]);

  return (
    <div className="prototype-planet" aria-hidden="true">
      <canvas ref={canvasRef} className="prototype-planet-canvas" />
      <div className="planet-judgment-atmosphere" />
      <svg className="planet-judgment-field" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
        <circle className="judgment-boundary boundary-outer" cx="50" cy="50" r="45" />
        <circle className="judgment-boundary boundary-middle" cx="50" cy="50" r="34.5" />
        <circle className="judgment-core-ring core-ring-a" cx="50" cy="50" r="22" />
        <circle className="judgment-core-ring core-ring-b" cx="50" cy="50" r="25.2" />
        <g className="judgment-force force-empathy" data-force="empathy">
          <ellipse className="judgment-orbit" cx="50" cy="50" rx="38.5" ry="19" transform="rotate(20 50 50)" pathLength="1" />
          <path className="judgment-signal judgment-signal-bed" d="M 14 35 C 27 35, 39 42, 50 50" />
          <path className="judgment-signal judgment-signal-flow" d="M 14 35 C 27 35, 39 42, 50 50" pathLength="1" />
          <circle className="judgment-node-aura" cx="14" cy="35" r="6.4" />
          <circle className="judgment-node-wave" cx="14" cy="35" r="4.55" />
          <circle className="judgment-node-halo" cx="14" cy="35" r="2.8" />
          <circle className="judgment-node" cx="14" cy="35" r="0.88" />
        </g>
        <g className="judgment-force force-nuance" data-force="nuance">
          <ellipse className="judgment-orbit" cx="50" cy="50" rx="38.5" ry="19" transform="rotate(-20 50 50)" pathLength="1" />
          <path className="judgment-signal judgment-signal-bed" d="M 86 35 C 73 35, 61 42, 50 50" />
          <path className="judgment-signal judgment-signal-flow" d="M 86 35 C 73 35, 61 42, 50 50" pathLength="1" />
          <circle className="judgment-node-aura" cx="86" cy="35" r="6.4" />
          <circle className="judgment-node-wave" cx="86" cy="35" r="4.55" />
          <circle className="judgment-node-halo" cx="86" cy="35" r="2.8" />
          <circle className="judgment-node" cx="86" cy="35" r="0.88" />
        </g>
        <g className="judgment-force force-accountability" data-force="accountability">
          <ellipse className="judgment-orbit" cx="50" cy="50" rx="25" ry="40" pathLength="1" />
          <path className="judgment-signal judgment-signal-bed" d="M 50 90 C 50 74, 50 62, 50 50" />
          <path className="judgment-signal judgment-signal-flow" d="M 50 90 C 50 74, 50 62, 50 50" pathLength="1" />
          <circle className="judgment-node-aura" cx="50" cy="90" r="6.4" />
          <circle className="judgment-node-wave" cx="50" cy="90" r="4.55" />
          <circle className="judgment-node-halo" cx="50" cy="90" r="2.8" />
          <circle className="judgment-node" cx="50" cy="90" r="0.88" />
        </g>
      </svg>
      <div className="judgment-force-label label-empathy" data-force="empathy">
        <strong>Empathy</strong>
        <span>Human stakes recognized.</span>
      </div>
      <div className="judgment-force-label label-nuance" data-force="nuance">
        <strong>Nuance</strong>
        <span>Meaning beyond the rule.</span>
      </div>
      <div className="judgment-force-label label-accountability" data-force="accountability">
        <strong>Accountability</strong>
        <span>The outcome is owned.</span>
      </div>
      <div className="planet-speed-field" />
      <div className="planet-speed-pulse" />
      {webglFailed && (
        <>
          <div className="prototype-planet-fallback">
            <span /><span /><span /><span />
          </div>
          <svg className="human-earth-fallback" viewBox="0 0 100 100" aria-hidden="true">
            <defs>
              <radialGradient id="humanEarthOcean" cx="31%" cy="25%" r="74%">
                <stop offset="0" stopColor="#2b85b4" />
                <stop offset="0.38" stopColor="#0e527e" />
                <stop offset="0.72" stopColor="#082d52" />
                <stop offset="1" stopColor="#020c1d" />
              </radialGradient>
              <radialGradient id="humanEarthLight" cx="27%" cy="22%" r="82%">
                <stop offset="0" stopColor="#ffffff" stopOpacity="0.34" />
                <stop offset="0.44" stopColor="#8dd6f4" stopOpacity="0.08" />
                <stop offset="0.72" stopColor="#021329" stopOpacity="0.18" />
                <stop offset="1" stopColor="#000713" stopOpacity="0.86" />
              </radialGradient>
              <filter id="humanEarthAtmosphere" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="1.35" />
              </filter>
              <clipPath id="humanEarthDisc"><circle cx="50" cy="50" r="47.4" /></clipPath>
            </defs>
            <circle className="human-earth-atmosphere" cx="50" cy="50" r="48.3" filter="url(#humanEarthAtmosphere)" />
            <g clipPath="url(#humanEarthDisc)">
              <circle cx="50" cy="50" r="47.4" fill="url(#humanEarthOcean)" />
              <image
                className="human-earth-surface-map"
                href={getAssetUrl("/planet/human/albedo.webp")}
                x="2.6"
                y="2.6"
                width="94.8"
                height="94.8"
                preserveAspectRatio="xMidYMid slice"
              />
              <circle cx="50" cy="50" r="47.4" fill="url(#humanEarthLight)" />
            </g>
            <circle className="human-earth-rim" cx="50" cy="50" r="47.2" />
          </svg>
        </>
      )}
      <div className="planet-core-mark">
        <Image src="/fto-logo.svg" alt="" width={500} height={500} draggable={false} priority />
      </div>
    </div>
  );
}
